import { SharedConfig } from '@shared/SharedConfig';

export interface EnergyProgramForm {
  basePointsPerGallon?: number,
  categoryMappingId: string | null,
  endDate: string | null,
  name: string,
  pointsExpirationDate: string | null,
  startDate: string | null,
}

export type EnergyProgramFormErrorKey =
  'baseRate' | 'endBeforeStart' | 'expirationBeforeEnd' | 'nameRequired'
  | 'overlapsEnergyProgram';

export interface EnergyProgramPeriod {
  endDate: string,
  startDate: string,
}

export type EnergyProgramFormErrors = Partial<
  Record<keyof EnergyProgramForm, EnergyProgramFormErrorKey>
>;

const MAX_NAME_LENGTH = 100;
const MAX_TIER_NAME_LENGTH = 50;
const MAX_BEHAVIOR_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 300;
const MAX_SKU_LENGTH = 50;

const hasAtMostDecimals = (value: number, decimals: number) => {
  const scale = 10 ** decimals;
  return Math.abs(value * scale - Math.round(value * scale)) < 1e-9;
};

export const validateEnergyProgramForm = (
  form: EnergyProgramForm,
  otherEnergyPrograms: EnergyProgramPeriod[] = [],
): EnergyProgramFormErrors => {
  const errors: EnergyProgramFormErrors = {};
  const name = form.name.trim();
  if (!name || name.length > MAX_NAME_LENGTH) {
    errors.name = 'nameRequired';
  }
  const { endDate, startDate } = form;
  if (startDate && endDate) {
    if (endDate < startDate) {
      errors.endDate = 'endBeforeStart';
    } else if (otherEnergyPrograms.some((program) => (
      startDate <= program.endDate && endDate >= program.startDate
    ))) {
      errors.startDate = 'overlapsEnergyProgram';
    }
  }
  if (form.endDate && form.pointsExpirationDate && form.pointsExpirationDate < form.endDate) {
    errors.pointsExpirationDate = 'expirationBeforeEnd';
  }
  const rate = form.basePointsPerGallon;
  if (rate === undefined || !(rate > 0) || !hasAtMostDecimals(rate, 4)) {
    errors.basePointsPerGallon = 'baseRate';
  }
  return errors;
};

export const isEnergyProgramFormComplete = (
  form: EnergyProgramForm,
  otherEnergyPrograms: EnergyProgramPeriod[] = [],
) => (
  !!form.startDate && !!form.endDate && !!form.pointsExpirationDate
  && !Object.keys(validateEnergyProgramForm(form, otherEnergyPrograms)).length
);

export interface EnergyBehaviorForm {
  completionCount: number,
  description: string,
  id?: string,
  isRequiredForTier: boolean,
  name: string,
  sku: string,
}

export interface EnergyTierForm {
  behaviors: EnergyBehaviorForm[],
  behaviorsRequired: number,
  name: string,
  pointsMultiplier: number,
}

export type EnergyTierErrorKey =
  | 'baseMultiplierFixed'
  | 'behaviorNameRequired'
  | 'behaviorNamesNotUnique'
  | 'behaviorsRequiredBelowRequired'
  | 'behaviorsRequiredExceedsDefined'
  | 'behaviorsRequiredForNonBase'
  | 'descriptionRequired'
  | 'multipliersNotAscending'
  | 'noBehaviorsDefined'
  | 'skuTooLong'
  | 'tierNameRequired'
  | 'tierNameTooLong'
  | 'tierNamesNotUnique';

export interface EnergyTierError {
  behaviorIndex?: number,
  key: EnergyTierErrorKey,
  tierIndex: number,
}

const countRequiredBehaviors = (tier: EnergyTierForm) => (
  tier.behaviors.filter(({ isRequiredForTier }) => isRequiredForTier).length
);

// Behaviors marked required count toward the tier's own behaviors required.
export const raiseBehaviorsRequiredToRequiredCount = (
  tiers: EnergyTierForm[],
): EnergyTierForm[] => tiers.map((tier) => {
  const requiredCount = countRequiredBehaviors(tier);
  return tier.behaviorsRequired < requiredCount
    ? { ...tier, behaviorsRequired: requiredCount }
    : tier;
});

// Reaching a tier means meeting every tier below it too.
export const totalBehaviorsRequired = (tiers: EnergyTierForm[], tierIndex: number) => (
  tiers.slice(0, tierIndex + 1).reduce((total, tier) => total + tier.behaviorsRequired, 0)
);

// Mirrors the server rules so the admin sees the same message before saving.
export const validateEnergyTiers = (tiers: EnergyTierForm[]): EnergyTierError | null => {
  const tierNames = new Set<string>();
  const behaviorNames = new Set<string>();
  for (const [tierIndex, tier] of tiers.entries()) {
    const fail = (key: EnergyTierErrorKey, behaviorIndex?: number) => (
      { behaviorIndex, key, tierIndex }
    );
    const name = tier.name.trim();
    if (!name) {
      return fail('tierNameRequired');
    }
    if (name.length > MAX_TIER_NAME_LENGTH) {
      return fail('tierNameTooLong');
    }
    if (tierNames.has(name.toLowerCase())) {
      return fail('tierNamesNotUnique');
    }
    tierNames.add(name.toLowerCase());
    const isBaseTier = tierIndex === 0;
    if (isBaseTier && tier.pointsMultiplier !== 1) {
      return fail('baseMultiplierFixed');
    }
    const previous = tiers[tierIndex - 1];
    if (
      tier.pointsMultiplier < 1
      || !hasAtMostDecimals(tier.pointsMultiplier, 1)
      || (!!previous && tier.pointsMultiplier <= previous.pointsMultiplier)
    ) {
      return fail('multipliersNotAscending');
    }
    const minimumRequired = isBaseTier ? 0 : 1;
    if (!Number.isInteger(tier.behaviorsRequired) || tier.behaviorsRequired < minimumRequired) {
      return fail('behaviorsRequiredForNonBase');
    }
    if (!isBaseTier && !tier.behaviors.length) {
      return fail('noBehaviorsDefined');
    }
    if (tier.behaviorsRequired > tier.behaviors.length) {
      return fail('behaviorsRequiredExceedsDefined');
    }
    if (tier.behaviorsRequired < countRequiredBehaviors(tier)) {
      return fail('behaviorsRequiredBelowRequired');
    }
    for (const [behaviorIndex, behavior] of tier.behaviors.entries()) {
      const behaviorName = behavior.name.trim();
      if (!behaviorName || behaviorName.length > MAX_BEHAVIOR_NAME_LENGTH) {
        return fail('behaviorNameRequired', behaviorIndex);
      }
      if (behaviorNames.has(behaviorName.toLowerCase())) {
        return fail('behaviorNamesNotUnique', behaviorIndex);
      }
      behaviorNames.add(behaviorName.toLowerCase());
      const description = behavior.description.trim();
      if (!description || description.length > MAX_DESCRIPTION_LENGTH) {
        return fail('descriptionRequired', behaviorIndex);
      }
      if (behavior.sku.trim().length > MAX_SKU_LENGTH) {
        return fail('skuTooLong', behaviorIndex);
      }
    }
  }
  return null;
};

export const effectiveRate = (basePointsPerGallon: number, multiplier: number) => {
  const points = Math.round(basePointsPerGallon * multiplier * 10000) / 10000;
  const cost = Math.round(points * SharedConfig.settings.energyPointValueDollars * 10000) / 10000;
  return { cost, points };
};
